import { logger } from '../utils/logger';
/**
 * Input Validation System
 * 
 * Provides comprehensive validation and sanitization for user inputs
 * to prevent XSS attacks and ensure data integrity.
 */

export interface ValidationResult {
  isValid: boolean;
  sanitized: string;
  errors: string[];
}

export interface ValidationOptions {
  maxLength?: number;
  allowHtml?: boolean;
  allowedTags?: string[];
  allowedAttributes?: string[];
}

/**
 * Validate and sanitize embed input
 */
export const validateEmbedInput = async (
  input: string, 
  options: ValidationOptions = {}
): Promise<ValidationResult> => {
  const {
    maxLength = 10000,
    allowHtml = true,
    allowedTags = ['iframe', 'blockquote', 'script', 'a', 'p', 'br', 'div', 'span'],
    allowedAttributes = ['src', 'width', 'height', 'frameborder', 'allowfullscreen', 'scrolling', 'class', 'data-*', 'href', 'target', 'rel']
  } = options;
  
  const errors: string[] = [];
  let sanitized = input.trim();
  
  // Length validation
  if (sanitized.length > maxLength) {
    errors.push(`Input too long (max ${maxLength} characters)`);
    sanitized = sanitized.substring(0, maxLength);
  }
  
  // Empty input validation
  if (sanitized.length === 0) {
    errors.push('Input cannot be empty');
    return { isValid: false, sanitized: '', errors };
  }
  
  // URL validation for HTTP inputs
  if (sanitized.startsWith('http')) {
    try {
      const url = new URL(sanitized);
      if (!['http:', 'https:'].includes(url.protocol)) {
        errors.push('Invalid protocol - only HTTP and HTTPS allowed');
      }
      
      // Check for suspicious domains
      const suspiciousDomains = ['javascript:', 'data:', 'vbscript:'];
      if (suspiciousDomains.some(domain => sanitized.toLowerCase().includes(domain))) {
        errors.push('Suspicious content detected');
      }
    } catch {
      errors.push('Invalid URL format');
    }
  }
  
  // XSS prevention - only block truly dangerous patterns
  // Allow legitimate social media embed scripts and iframes
  const dangerousPatterns = [
    /javascript:(?!void\(0\))/gi, // Allow javascript:void(0) but block other JS protocols
    /vbscript:/gi,
    /on\w+\s*=\s*["'][^"']*["']/gi, // Only block event handlers with actual content
    /<iframe[^>]*src\s*=\s*["']?javascript:(?!void\(0\))/gi
  ];
  
  // Don't block script tags entirely - social media embeds need them
  // Instead, validate that they're from trusted domains
  if (sanitized.includes('<script')) {
    const scriptTags = sanitized.match(/<script[^>]*src\s*=\s*["']([^"']*)["'][^>]*>/gi);
    if (scriptTags) {
      const trustedDomains = [
        'platform.twitter.com',
        'www.instagram.com', 
        'www.tiktok.com',
        'www.youtube.com',
        'www.facebook.com',
        'www.linkedin.com'
      ];
      
      for (const script of scriptTags) {
        const srcMatch = script.match(/src\s*=\s*["']([^"']*)["']/);
        if (srcMatch) {
          const src = srcMatch[1];
          const isTrusted = trustedDomains.some(domain => src.includes(domain));
          if (!isTrusted) {
            errors.push('Script source not from trusted domain');
          }
        }
      }
    }
  }
  
  dangerousPatterns.forEach(pattern => {
    if (pattern.test(sanitized)) {
      errors.push('Potentially dangerous content detected');
      sanitized = sanitized.replace(pattern, '');
    }
  });
  
  // HTML sanitization if needed
  if (allowHtml && sanitized.includes('<')) {
    try {
      // Import DOMPurify dynamically to avoid SSR issues
      const DOMPurify = await import('dompurify');
      sanitized = DOMPurify.default.sanitize(sanitized, {
        ALLOWED_TAGS: allowedTags,
        ALLOWED_ATTR: allowedAttributes,
        ALLOW_DATA_ATTR: true,
        ALLOW_UNKNOWN_PROTOCOLS: false,
        SANITIZE_DOM: true,
        KEEP_CONTENT: true
      });
    } catch (error) {
      // If DOMPurify fails, just log and continue (don't block the user)
      if (import.meta.env.DEV) {
        logger.warn('DOMPurify sanitization failed:', error);
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    sanitized,
    errors
  };
};

/**
 * Validate asset title
 */
export const validateAssetTitle = (title: string): ValidationResult => {
  const errors: string[] = [];
  let sanitized = title.trim();
  
  if (sanitized.length === 0) {
    errors.push('Title cannot be empty');
  }
  
  if (sanitized.length > 200) {
    errors.push('Title too long (max 200 characters)');
    sanitized = sanitized.substring(0, 200);
  }
  
  // Remove HTML tags from titles
  sanitized = sanitized.replace(/<[^>]*>/g, '');
  
  // Remove dangerous characters
  sanitized = sanitized.replace(/[<>'"&]/g, '');
  
    return { 
    isValid: errors.length === 0,
    sanitized,
    errors
  };
};

/**
 * Validate date input
 */
export const validateDate = (dateString: string): ValidationResult => {
  const errors: string[] = [];
  let sanitized = dateString.trim();
  
  if (!sanitized) {
    errors.push('Date is required');
    return { isValid: false, sanitized: '', errors };
  }
  
  const date = new Date(sanitized);
  if (isNaN(date.getTime())) {
    errors.push('Invalid date format');
  }
  
  // Check if date is in the future (more than 1 year)
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
  
  if (date > oneYearFromNow) {
    errors.push('Date cannot be more than 1 year in the future');
  }
  
  // Check if date is too far in the past (more than 10 years)
  const tenYearsAgo = new Date();
  tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
  
  if (date < tenYearsAgo) {
    errors.push('Date cannot be more than 10 years in the past');
  }
  
    return { 
    isValid: errors.length === 0,
    sanitized,
    errors
  };
};

/**
 * Sanitize user input for display
 */
export const sanitizeForDisplay = (input: string): string => {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Validate email input
 */
export const validateEmail = (email: string): ValidationResult => {
  const errors: string[] = [];
  const sanitized = email.trim().toLowerCase();
  
  if (!sanitized) {
    errors.push('Email is required');
    return { isValid: false, sanitized: '', errors };
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(sanitized)) {
    errors.push('Invalid email format');
  }
  
  if (sanitized.length > 254) {
    errors.push('Email too long (max 254 characters)');
  }
  
    return { 
    isValid: errors.length === 0,
    sanitized,
    errors
  };
};
